import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { reilmAPI } from '../services/apiClient'
import SearchCriteria from '../features/arv/SearchCriteria'
import ArvMap from '../features/arv/ArvMap'
import ComparableFilterRow from '../features/arv/ComparableFilterRow'
import ComparableGrid from '../features/arv/ComparableGrid'
import VerifyGrid from '../features/arv/VerifyGrid'
import { mockComps } from '../features/arv/mockComps'
import EstimateMetrics from '../features/arv/EstimateMetrics'
import GalleryModal from '../components/GalleryModal'
import PageShell from '../layouts/PageShell'
import AppNavbar from '../features/AppNavbar'

function getCompStrength(avg) {
  if (avg === null) return { label: null, sub: null }
  if (avg >= 85) return { label: 'Very Strong',  sub: 'Reliable, direct comps' }
  if (avg >= 80) return { label: 'Strong',        sub: 'Reliable comps' }
  if (avg >= 75) return { label: 'Moderate',      sub: 'Reasonable comps' }
  if (avg >= 70) return { label: 'Weak',          sub: 'Less reliable comps' }
  return           { label: 'Very Weak',   sub: 'Insufficient comps' }
}

function initCriteria(saved, subj) {
  return {
    propertyType: saved?.propertyType != null ? String(saved.propertyType) : String(subj?.propertyType ?? 'single_family'),
    beds:         saved?.beds != null ? String(saved.beds) : String(subj?.beds ?? ''),
    baths:        saved?.baths != null ? String(saved.baths) : String(subj?.baths ?? ''),
    sqftMin:      saved?.sqftMin != null ? String(saved.sqftMin)
                    : subj?.sqft ? subj.sqft : '',
    yearBuiltMin: saved?.yearBuiltMin != null ? String(saved.yearBuiltMin)
                    : subj?.yearBuilt ? String(subj.yearBuilt) : '',
    monthsWithin: String(saved?.monthsWithin ?? '12'),
    radiusMiles:  String(saved?.radiusMiles ?? '1'),
  }
}

function applyBaseFilters(sourceComps, f) {
  return sourceComps.filter(c => {
    if (f.status === 'Sold'   && c.status?.toLowerCase() !== 'sold') return false
    if (f.status === 'Active' && c.status?.toLowerCase() === 'sold') return false
    const pricek = (c.price ?? 0) / 1000
    if (f.priceMin && pricek < Number(f.priceMin)) return false
    if (f.priceMax && pricek > Number(f.priceMax)) return false
    const sqft = c.specs?.sqft ?? 0
    if (f.sqftMin && sqft < Number(f.sqftMin)) return false
    if (f.sqftMax && sqft > Number(f.sqftMax)) return false
    return true
  })
}

export default function Arv() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  const routeProp = location.state?.property ?? null
  const savedCriteria = routeProp?.arv?.criteria ?? null

  const initComps = (routeProp?.arv?.comps?.length ? routeProp.arv.comps : []).map(c => ({ ...c }))
  const initFilters = routeProp?.arv?.filters ?? {
    status: 'All',
    priceMin: '',
    priceMax: '',
    sqftMin: String(Math.round((routeProp?.details?.sqft * 0.6) / 100) * 100),
    sqftMax: String(Math.round((routeProp?.details?.sqft * 1.4) / 100) * 100),
    archivedView: 'active',
    visible: 10,
  }

  const [property, setProperty] = useState(routeProp)
  const [arvData, setArvData] = useState(routeProp?.arv ?? null)
  const [loading, setLoading] = useState(!routeProp)
  const [criteria, setCriteria] = useState(() => initCriteria(savedCriteria, { ...routeProp?.details, propertyType: routeProp?.propertyType }))
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState(null)
  const [filters, setFilters] = useState(initFilters)
  const [comps, setComps] = useState(initComps)
  const [galleryPhotos, setGalleryPhotos] = useState(null)

  useEffect(() => {
    if (property) return
    reilmAPI.getProperty(id)
      .then(res => {
        const prop = res.data?.data?.property
        setProperty(prop)
        const arv = prop?.arv ?? null
        setArvData(arv)
        setCriteria(initCriteria(arv?.criteria ?? null, prop?.details))
        const loadedFilters = arv?.filters ?? {
          status: 'All', priceMin: '', priceMax: '',
          sqftMin: String(Math.round((prop?.details?.sqft * 0.6) / 100) * 100),
          sqftMax: String(Math.round((prop?.details?.sqft * 1.4) / 100) * 100),
          archivedView: 'active', visible: 10,
        }
        if (arv?.filters) setFilters(loadedFilters)
        const loaded = arv?.comps?.length ? arv.comps : mockComps
        setComps(loaded.map(c => ({ ...c })))
      })
      .catch(err => console.error('getProperty error', err))
      .finally(() => setLoading(false))
  }, [id])

  function buildPayload(c) {
    const num = (v) => (v === '' || v == null ? null : Number(v))
    return {
      propertyType: c.propertyType || null,
      beds:         num(c.beds),
      baths:        num(c.baths),
      sqftMin:      num(c.sqftMin),
      sqftMax:      num(c.sqftMax),
      yearBuiltMin: num(c.yearBuiltMin),
      monthsWithin: num(c.monthsWithin) ?? 12,
      radiusMiles:  num(c.radiusMiles)  ?? 1.0,
    }
  }

  const handleSearch = async () => {
    setSearching(true)
    setSearchError(null)
    try {
      const res = await reilmAPI.searchArvComps(id, buildPayload(criteria))
      const arv = res.data?.data?.arv ?? null
      setArvData(arv)
      setComps((arv?.comps ?? []).map(c => ({ ...c })))
      setFilters(f => ({ ...f, visible: 10 }))
    } catch (err) {
      setSearchError(err.response?.data?.error?.message ?? 'Search failed. Please try again.')
    } finally {
      setSearching(false)
    }
  }

  const handleToggleArchived = async (row) => {
    const next = !row.archived
    setComps(prev => prev.map(c =>
      c.propertyId === row.propertyId ? { ...c, archived: next } : c
    ))
    try {
      const res = await reilmAPI.toggleArvComp(id, row.propertyId, next)
      const arv = res.data?.data?.arv ?? null
      if (arv) {
        setArvData(arv)
        setComps(arv.comps.map(c => ({ ...c })))
      }
    } catch {
      setComps(prev => prev.map(c =>
        c.propertyId === row.propertyId ? { ...c, archived: row.archived } : c
      ))
    }
  }

  const handleCriteriaPersist = (updatedCriteria) => {
    reilmAPI.updatedPropertiesDetails(id, { 'arv.criteria': buildPayload(updatedCriteria) })
      .catch(err => console.error('criteria persist', err))
  }

  const handleFiltersChange = (newFilters) => {
    // View toggle: keep visible as-is, just switch which side is shown
    if (newFilters.archivedView !== filters.archivedView) {
      setFilters(newFilters)
      return
    }
    // Real filter change (status, price, sqft): reset visible to 10
    setFilters({ ...newFilters, visible: 10 })
  }

  const handleLoadMore = () => {
    const next = { ...filters, visible: (filters.visible ?? 10) + 5 }
    setFilters(next)
    handleFiltersPersist(next)
  }

  const handleFiltersPersist = (updatedFilters) => {
    reilmAPI.updatedPropertiesDetails(id, { 'arv.filters': updatedFilters })
      .catch(err => console.error('filters persist', err))
  }

  const addressDisplay = property ? [
    property.address?.street,
    property.address?.city,
    property.address?.state,
    property.address?.zip,
  ].filter(Boolean).join(', ') : ''

  // Slice before filtering archived so active + archived always equals filters.visible.
  // This prevents backfill: archiving a comp shrinks the active count rather than pulling
  // in the next comp from outside the window.
  const windowComps = applyBaseFilters(comps, filters).slice(0, filters.visible ?? 10)
  const displayedComps = filters.archivedView === 'archived'
    ? windowComps.filter(c => c.archived)
    : windowComps.filter(c => !c.archived)

  // Pill counts from the window — active + archived = visible
  const visibleCount = windowComps.filter(c => !c.archived).length
  const hiddenCount  = windowComps.filter(c =>  c.archived).length

  // baseFiltered: all comps matching filters (no pagination) — for Load More gating + Results count
  const baseFiltered = applyBaseFilters(comps, filters)
  const filteredActiveComps = baseFiltered.filter(c => !c.archived)

  const statusOnlyComps = comps.filter(c => {
    if (filters.status === 'Sold'   && c.status?.toLowerCase() !== 'sold') return false
    if (filters.status === 'Active' && c.status?.toLowerCase() === 'sold') return false
    return true
  })

  // ARV: all active comps matching status filter — pagination/price/sqft do not affect the estimate
  const activeStatusComps = statusOnlyComps.filter(c => !c.archived)//can remove
  const liveArv = displayedComps.length
    ? Math.round(displayedComps.reduce((sum, c) => sum + (c.adjustments ?? 0), 0) / displayedComps.length)
    : null

  const avgScore = displayedComps.length
    ? displayedComps.reduce((sum, c) => sum + (c.score ?? 0), 0) / displayedComps.length
    : null
  const { label: strengthLabel, sub: strengthSub } = getCompStrength(avgScore)

  const domVals = displayedComps.map(c => c.dom).filter(v => v != null).sort((a, b) => a - b)
  const domMid  = Math.floor(domVals.length / 2)
  const medianDom = domVals.length === 0 ? null
    : domVals.length % 2 !== 0
      ? domVals[domMid]
      : Math.round((domVals[domMid - 1] + domVals[domMid]) / 2)

  const metrics = {
    arv: liveArv,
    compCount: statusOnlyComps.length,
    strengthLabel,
    strengthSub,
    medianDom,
  }

  return (
    <PageShell>
      <AppNavbar />
      <div className="min-h-screen bg-brand-pageFill">
        <div className="mt-[24px] flex flex-col gap-y-[20px] pb-[60px]">

          {/* Header */}
          <div className="flex flex-col gap-y-[8px]">
            <button
              onClick={() => navigate(`/property/${id}`)}
              className="self-start text-[13px] font-medium text-brand-green hover:opacity-70 transition-opacity"
            >
              ← Back
            </button>
            <div className="flex items-center gap-x-[8px]">
              <h1 className="text-[20px] font-bold text-brand-dark">ARV Analysis</h1>
              <div className="relative group">
                <span className="text-[13px] text-brand-secondary underline cursor-default">How to Use</span>
                <div
                  className="absolute z-20 hidden group-hover:block bottom-full left-0 mb-[8px] bg-white text-brand-dark rounded-[8px] p-[14px] text-[13px] leading-[1.5] shadow-lg pointer-events-none"
                  style={{ width: 450 }}
                >
                  <div>
                    1. Start by searching for comparable properties. 
                  </div>
                  <div>
                    2. Review the top-ranked comps, then narrow the list by hiding less relevant properties. ARV Estimate, Comps Strength, and Median DOM update automatically based on the comps currently shown.
                  </div>
                  <div>
                    3. Once refined, share the analysis with buyers, partners, or lenders.
                  </div>
                </div>
              </div>
            </div>
            {loading
              ? <div className="h-[20px] w-[220px] bg-brand-muted rounded animate-pulse" />
              : <p className="text-[14px] font-light text-brand-dark">{addressDisplay}</p>
            }
          </div>

          {loading ? (
            <div className="flex flex-col gap-y-[16px]">
              <div className="h-[160px] bg-brand-muted rounded-2xl animate-pulse" />
              <div className="h-[600px] bg-brand-muted rounded-2xl animate-pulse" />
              <div className="h-[400px] bg-brand-muted rounded-2xl animate-pulse" />
            </div>
          ) : (
            <>
              {/* Search Criteria */}
              <SearchCriteria
                criteria={criteria}
                onCriteriaChange={setCriteria}
                onCriteriaBlur={handleCriteriaPersist}
                onSearch={handleSearch}
                searching={searching}
                lastSearchedAt={arvData?.searchedAt ?? null}
              />
              {searchError && (
                <p className="text-[12px] text-red-500">{searchError}</p>
              )}

              {/* Map */}
              <div className="relative">
                <ArvMap
                  coordinates={property?.address?.coordinates}
                  comps={displayedComps}
                  onListingClick={(comp) => {
                    const idx = displayedComps.findIndex(c => c.propertyId === comp.propertyId)
                    navigate(`/property/${id}/arv/listing/${idx}`, { state: { comp, filteredComps: displayedComps, compIndex: idx } })
                  }}
                  onGalleryOpen={(photos) => setGalleryPhotos(photos)}
                  onToggleArchived={handleToggleArchived}
                  lastSearchedAt={arvData?.searchedAt ?? null}
                />
                {searching && (
                  <div className="absolute inset-0 bg-white/80 rounded-2xl z-10 pointer-events-none animate-pulse" />
                )}
              </div>

              {/* Filter row — full width */}
              <ComparableFilterRow
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onFiltersBlur={handleFiltersPersist}
                visibleCount={visibleCount}
                hiddenCount={hiddenCount}
                gridCount={baseFiltered.length}
              />

              {/* Grid + Sidebar */}
              <div className="flex gap-x-[15px]">

                {/* Left — grid */}
                <div className="relative flex flex-col gap-y-[16px] border border-brand-fieldBdr rounded-2xl " style={{ minwidth: 885, height:'100%' }}>
                  <div className="bg-white rounded-2xl overflow-hidden">
                    <ComparableGrid
                      rows={displayedComps}
                      onRowClick={(row) => {
                        const idx = displayedComps.findIndex(c => c.propertyId === row.propertyId)
                        navigate(`/property/${id}/arv/listing/${idx}`, { state: { comp: row, filteredComps: displayedComps, compIndex: idx } })
                      }}
                      onToggleArchived={handleToggleArchived}
                      onGalleryOpen={(photos) => setGalleryPhotos(photos)}
                    />
                    <button
                      onClick={handleLoadMore}
                      disabled={filteredActiveComps.length <= (filters.visible ?? 10) || filters.archivedView === 'archived'}
                      className="w-full h-[72px] flex items-center justify-center  border-t border-brand-muted text-[13px] font-semibold text-brand-green hover:bg-[#E8F5E1] transition-colors disabled:bg-[#FFFFFF] disabled:text-[#8A9A85] disabled:cursor-not-allowed"
                    >
                      Load More Comps
                    </button>
                    {searching && (
                      <div className="absolute inset-0 bg-white/80 rounded-2xl z-10 pointer-events-none animate-pulse" />
                    )}
                  </div>
                </div>

                {/* Right — estimate sidebar */}
                <div>
                  <EstimateMetrics metrics={metrics} />
                  
                </div>
              </div>
              
            </>
          )}

        </div>
      </div>
      {galleryPhotos && (
        <GalleryModal photos={galleryPhotos} onClose={() => setGalleryPhotos(null)} />
      )}
    </PageShell>
  )
}
